/**
 * Server Operations Domain
 * Types, store, and handlers for server-side operations monitoring
 * (serialization, send to server, world save, manifest tick), which the
 * performance panel lists as they run, since the player joined the match.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { oneShotClock } from '../utils/one-shot-clock';

// ==================== TYPES ====================

interface OperationInfo {
  isProcessing: boolean;
  startedAtUnix: number | null;
  durationMs: number | null;
  gameTick: number | null;
  completedAt: number | null; // Date.now() when its end arrived: the panel lists an operation once it has run
}

const createEmptyOperationInfo = (): OperationInfo => ({
  isProcessing: false,
  startedAtUnix: null,
  durationMs: null,
  gameTick: null,
  completedAt: null,
});

// ==================== STORE ====================

interface ServerOperationsState {
  serialization: OperationInfo;
  sendToServer: OperationInfo;
  worldSave: OperationInfo;
  manifestTick: OperationInfo;

  handleWorldSavePrep: (data: {
    phase: 'serializing' | 'serialized' | 'sent';
    started_at_unix: number;
    game_tick: number;
    serialization_ms?: number;
    data_size_bytes?: number;
    compression_ms?: number;
    send_ms?: number;
    total_prep_ms?: number;
  }) => void;
  handleWorldSaveStart: (startedAtUnix: number, gameTick: number) => void;
  handleWorldSaveEnd: (startedAtUnix: number, durationMs: number, gameTick: number) => void;
  handleManifestTickStart: (startedAtUnix: number, gameTick: number) => void;
  handleManifestTickEnd: (startedAtUnix: number, durationMs: number, gameTick: number) => void;
  /** Forget them all: the player has left the match (connection-reset.ts). */
  reset: () => void;
}

const prepClock = oneShotClock();
const PREP_AUTO_CLEAR_MS = 30_000;

function startPrepTimeout() {
  prepClock.start(PREP_AUTO_CLEAR_MS, () => {
    useServerOperationsStore.setState(
      { serialization: createEmptyOperationInfo(), sendToServer: createEmptyOperationInfo() },
      false,
      'handleWorldSavePrepTimeout'
    );
  });
}

export const useServerOperationsStore = create<ServerOperationsState>()(
  devtools(
    (set) => ({
      serialization: createEmptyOperationInfo(),
      sendToServer: createEmptyOperationInfo(),
      worldSave: createEmptyOperationInfo(),
      manifestTick: createEmptyOperationInfo(),

      handleWorldSavePrep: (data) => {
        prepClock.stop();

        switch (data.phase) {
          case 'serializing':
            set(
              {
                serialization: {
                  isProcessing: true,
                  startedAtUnix: data.started_at_unix,
                  durationMs: null,
                  gameTick: data.game_tick,
                  completedAt: null,
                },
                sendToServer: createEmptyOperationInfo(),
              },
              false,
              'handleWorldSavePrep/serializing'
            );
            startPrepTimeout();
            break;

          case 'serialized':
            set(
              {
                serialization: {
                  isProcessing: false,
                  startedAtUnix: data.started_at_unix,
                  durationMs: data.serialization_ms ?? null,
                  gameTick: data.game_tick,
                  completedAt: Date.now(),
                },
                sendToServer: {
                  isProcessing: true,
                  startedAtUnix: data.started_at_unix,
                  durationMs: null,
                  gameTick: data.game_tick,
                  completedAt: null,
                },
              },
              false,
              'handleWorldSavePrep/serialized'
            );
            startPrepTimeout();
            break;

          case 'sent': {
            const sendDuration =
              data.compression_ms != null && data.send_ms != null
                ? data.compression_ms + data.send_ms
                : data.total_prep_ms != null && data.serialization_ms != null
                  ? data.total_prep_ms - data.serialization_ms
                  : null;

            set(
              {
                sendToServer: {
                  isProcessing: false,
                  startedAtUnix: data.started_at_unix,
                  durationMs: sendDuration,
                  gameTick: data.game_tick,
                  completedAt: Date.now(),
                },
              },
              false,
              'handleWorldSavePrep/sent'
            );
            break;
          }
        }
      },

      handleWorldSaveStart: (startedAtUnix, gameTick) => {
        prepClock.stop();

        set(
          {
            worldSave: {
              isProcessing: true,
              startedAtUnix,
              durationMs: null,
              gameTick,
              completedAt: null,
            },
          },
          false,
          'handleWorldSaveStart'
        );
      },

      handleWorldSaveEnd: (startedAtUnix, durationMs, gameTick) => {
        prepClock.stop();

        set(
          {
            worldSave: {
              isProcessing: false,
              startedAtUnix,
              durationMs,
              gameTick,
              completedAt: Date.now(),
            },
          },
          false,
          'handleWorldSaveEnd'
        );
      },

      handleManifestTickStart: (startedAtUnix, gameTick) => {
        set(
          {
            manifestTick: {
              isProcessing: true,
              startedAtUnix,
              durationMs: null,
              gameTick,
              completedAt: null,
            },
          },
          false,
          'handleManifestTickStart'
        );
      },

      handleManifestTickEnd: (startedAtUnix, durationMs, gameTick) => {
        set(
          {
            manifestTick: {
              isProcessing: false,
              startedAtUnix,
              durationMs,
              gameTick,
              completedAt: Date.now(),
            },
          },
          false,
          'handleManifestTickEnd'
        );
      },

      reset: () => {
        prepClock.stop();
        set(
          {
            serialization: createEmptyOperationInfo(),
            sendToServer: createEmptyOperationInfo(),
            worldSave: createEmptyOperationInfo(),
            manifestTick: createEmptyOperationInfo(),
          },
          false,
          'reset'
        );
      },
    }),
    { name: 'server-operations-store' }
  )
);

// ==================== HANDLERS ====================

export function handleWorldSavePrep(data: {
  phase: 'serializing' | 'serialized' | 'sent';
  started_at_unix: number;
  game_tick: number;
  serialization_ms?: number;
  data_size_bytes?: number;
  compression_ms?: number;
  send_ms?: number;
  total_prep_ms?: number;
}): void {
  console.log('[ServerOps] World save prep:', data);
  useServerOperationsStore.getState().handleWorldSavePrep(data);
}

export function handleWorldSaveStart(data: {
  started_at_unix: number;
  game_tick: number;
}): void {
  console.log('[ServerOps] World save started:', data);
  useServerOperationsStore.getState().handleWorldSaveStart(data.started_at_unix, data.game_tick);
}

export function handleWorldSaveEnd(data: {
  started_at_unix: number;
  duration_ms: number;
  game_tick: number;
}): void {
  console.log('[ServerOps] World save completed:', data);
  useServerOperationsStore
    .getState()
    .handleWorldSaveEnd(data.started_at_unix, data.duration_ms, data.game_tick);
}

export function handleManifestTickStart(data: {
  started_at_unix: number;
  game_tick: number;
}): void {
  console.log('[ServerOps] Manifest tick started:', data);
  useServerOperationsStore
    .getState()
    .handleManifestTickStart(data.started_at_unix, data.game_tick);
}

export function handleManifestTickEnd(data: {
  started_at_unix: number;
  duration_ms: number;
  game_tick: number;
}): void {
  console.log('[ServerOps] Manifest tick completed:', data);
  useServerOperationsStore
    .getState()
    .handleManifestTickEnd(data.started_at_unix, data.duration_ms, data.game_tick);
}
